// X API BYOK Service
// ユーザーが自分のX API credentials を設定している場合に使う

const STORAGE_KEY = 'bangerfy_x_credentials';

export interface XCredentials {
  consumerKey: string;
  consumerSecret: string;
  accessToken: string;
  accessSecret: string;
}

export interface PostedTweet {
  historyId: string;   // Bangerfy History ID
  tweetId: string;     // X tweet ID
  postedAt: string;    // ISO timestamp
}

export interface TweetMetrics {
  id: string;
  likeCount: number;
  retweetCount: number;
  replyCount: number;
  impressionCount: number;
}

const POSTED_KEY = 'bangerfy_posted_tweets';

function getApiBase(): string {
  // Vercel: same origin. Local dev: proxy or relative
  return '';
}

export const xApiService = {
  // ── Credentials ──

  getCredentials(): XCredentials | null {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return null;
    try {
      const creds = JSON.parse(data);
      if (creds.consumerKey && creds.consumerSecret && creds.accessToken && creds.accessSecret) {
        return creds;
      }
      return null;
    } catch { return null; }
  },

  saveCredentials(creds: XCredentials): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(creds));
  },

  clearCredentials(): void {
    localStorage.removeItem(STORAGE_KEY);
  },

  hasCredentials(): boolean {
    return this.getCredentials() !== null;
  },

  // ── Post Tweet ──

  async postTweet(text: string): Promise<{ tweetId: string }> {
    const creds = this.getCredentials();
    if (!creds) throw new Error('X API credentials not configured');

    const res = await fetch(`${getApiBase()}/api/x-post`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Twitter-Consumer-Key': creds.consumerKey,
        'X-Twitter-Consumer-Secret': creds.consumerSecret,
        'X-Twitter-Access-Token': creds.accessToken,
        'X-Twitter-Access-Secret': creds.accessSecret,
      },
      body: JSON.stringify({ text }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `X API error: ${res.status}`);
    }

    const data = await res.json();
    return { tweetId: data.data.id };
  },

  // ── Get Metrics ──

  async getMetrics(tweetIds: string[]): Promise<TweetMetrics[]> {
    const creds = this.getCredentials();
    if (!creds) throw new Error('X API credentials not configured');

    const res = await fetch(`${getApiBase()}/api/x-metrics?ids=${tweetIds.join(',')}`, {
      method: 'GET',
      headers: {
        'X-Twitter-Consumer-Key': creds.consumerKey,
        'X-Twitter-Consumer-Secret': creds.consumerSecret,
        'X-Twitter-Access-Token': creds.accessToken,
        'X-Twitter-Access-Secret': creds.accessSecret,
      },
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `X API error: ${res.status}`);
    }

    const data = await res.json();
    return (data.data || []).map((t: any) => ({
      id: t.id,
      likeCount: t.public_metrics?.like_count ?? 0,
      retweetCount: t.public_metrics?.retweet_count ?? 0,
      replyCount: t.public_metrics?.reply_count ?? 0,
      impressionCount: t.public_metrics?.impression_count ?? 0,
    }));
  },

  // ── Posted Tweets Tracking ──

  getPostedTweets(): PostedTweet[] {
    const data = localStorage.getItem(POSTED_KEY);
    if (!data) return [];
    try { return JSON.parse(data); } catch { return []; }
  },

  trackPostedTweet(historyId: string, tweetId: string): void {
    const posted = this.getPostedTweets();
    posted.push({ historyId, tweetId, postedAt: new Date().toISOString() });
    // Keep last 200
    localStorage.setItem(POSTED_KEY, JSON.stringify(posted.slice(-200)));
  },

  // ── Auto-refresh metrics for posted tweets ──

  async refreshMetrics(): Promise<{ updated: number }> {
    const posted = this.getPostedTweets();
    if (posted.length === 0) return { updated: 0 };

    // Only refresh tweets posted in the last 7 days
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const recent = posted.filter(p => new Date(p.postedAt).getTime() > weekAgo);
    if (recent.length === 0) return { updated: 0 };

    const tweetIds = recent.map(p => p.tweetId);
    const metrics = await this.getMetrics(tweetIds);

    // Import dynamically to avoid circular deps
    const { storageService } = await import('./storageService');
    let updated = 0;

    for (const m of metrics) {
      const posted = recent.find(p => p.tweetId === m.id);
      if (!posted) continue;

      const totalEngagement = m.likeCount + m.retweetCount + m.replyCount;
      const items = storageService.getHistoryItems();
      const item = items.find(i => i.id === posted.historyId);
      if (!item) continue;

      // Auto-set feedback based on engagement
      let autoFeedback: 'banged' | 'normal' | 'flopped' | null = null;
      if (m.impressionCount > 0) {
        const engagementRate = totalEngagement / m.impressionCount;
        if (engagementRate >= 0.05 || m.likeCount >= 50) {
          autoFeedback = 'banged';
        } else if (engagementRate < 0.01 && m.likeCount < 5) {
          autoFeedback = 'flopped';
        } else {
          autoFeedback = 'normal';
        }
      } else if (m.likeCount >= 20) {
        autoFeedback = 'banged';
      } else if (m.likeCount < 3) {
        autoFeedback = 'flopped';
      }

      if (autoFeedback && !item.feedback) {
        storageService.setFeedback(posted.historyId, autoFeedback, m.likeCount);
        updated++;
      } else if (item.feedback && m.likeCount > (item.engagementLikes || 0)) {
        // Update likes count even if feedback was already set manually
        storageService.setFeedback(posted.historyId, item.feedback, m.likeCount);
        updated++;
      }
    }

    return { updated };
  },
};
